import { get, create, updatePatch, deleteResource } from "./apiService";

export interface BankAccount {
  _id: string;
  accountHolder: string;
  bankName: string;
  accountNumber: string;
  branchCode: string;
  accountType?: "savings" | "cheque";
  isPrimary?: boolean;
  [key: string]: unknown;
}

export interface CreateBankAccountPayload {
  bankId: string;
  accountHolder: string;
  bankName: string;
  accountNumber: string;
  branchCode: string;
  accountType: "savings" | "cheque";
}

export type BankAccountFormSource = Record<string, unknown>;

export function normalizeBankAccountType(
  raw: unknown
): CreateBankAccountPayload["accountType"] {
  const value = String(raw ?? "savings").toLowerCase();
  if (
    value === "cheque" ||
    value === "checking" ||
    value === "current" ||
    value === "transmission"
  ) {
    return "cheque";
  }
  return "savings";
}

export function buildCreateBankAccountPayload(
  source: CreateBankAccountPayload | BankAccountFormSource,
  options?: { accountHolderFallback?: string }
): CreateBankAccountPayload {
  const fields: Record<string, unknown> = { ...source };

  const selectedBank = fields.selectedBank as
    | Record<string, unknown>
    | undefined;
  const resolvedBankId = String(
    fields.bankId ?? selectedBank?.bankId ?? selectedBank?._id ?? ""
  ).trim();

  return {
    bankId: resolvedBankId,
    accountHolder: String(
      fields.accountHolder ??
        fields.recipientName ??
        options?.accountHolderFallback ??
        ""
    ).trim(),
    bankName: String(fields.bankName ?? fields.name ?? "").trim(),
    accountNumber: String(
      fields.accountNumber ?? fields.accNumber ?? ""
    ).trim(),
    branchCode: String(
      fields.branchCode ?? fields.universalBranchCode ?? fields.branch ?? ""
    ).trim(),
    accountType: normalizeBankAccountType(
      fields.accountType ?? fields.accType ?? fields.bankType
    ),
  };
}

export interface UpdateBankAccountPayload {
  accountHolder?: string;
  bankName?: string;
  accountNumber?: string;
  branchCode?: string;
  accountType?: "savings" | "cheque";
}

export interface BankAccountListResponse {
  bankAccounts: BankAccount[];
  total: number;
  page: number;
  limit: number;
  totalPages?: number;
  [key: string]: unknown;
}

export interface BankReference {
  reference: string;
  [key: string]: unknown;
}

export interface BankGuideEntry {
  title?: string;
  steps?: string[];
  footNote?: string;
}

export interface BankGuides {
  recurringTransfer?: BankGuideEntry;
  [key: string]: BankGuideEntry | undefined;
}

export interface BankInfo {
  _id: string;
  name: string;
  branchCode?: string;
  universalBranchCode?: string;
  country?: string;
  swiftCode?: string;
  iconUrl?: string;
  status?: "enabled" | "disabled" | string;
  guides?: BankGuides;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export const getBankAccounts = async (
  page = 1,
  limit = 20
): Promise<BankAccountListResponse> => {
  return get<BankAccountListResponse>("/api/v2/banking/bank-accounts", {
    page,
    limit,
  });
};

export const getBankAccount = async (id: string): Promise<BankAccount> => {
  return get<BankAccount>(`/api/v2/banking/bank-accounts/${id}`);
};

export const createBankAccount = async (
  data: CreateBankAccountPayload | BankAccountFormSource,
  options?: { accountHolderFallback?: string }
): Promise<BankAccount> => {
  const payload = buildCreateBankAccountPayload(data, options);

  if (!payload.bankId) {
    throw new Error("Select a bank before adding an account.");
  }

  return create<BankAccount>("/api/v2/banking/bank-accounts", payload);
};

export const updateBankAccount = async (
  id: string,
  data: UpdateBankAccountPayload
): Promise<BankAccount> => {
  return updatePatch<BankAccount>(
    `/api/v2/banking/bank-accounts/${id}`,
    {},
    data
  );
};

export const deleteBankAccount = async (id: string): Promise<unknown> => {
  return deleteResource(`/api/v2/banking/bank-accounts/${id}`);
};

export const getBankReference = async (id: string): Promise<BankReference> => {
  if (!id || typeof id !== "string" || !id.trim()) {
    throw new Error("Bank account ID is required to fetch a bank reference.");
  }
  return get<BankReference>(
    `/api/v2/banking/bank-accounts/${encodeURIComponent(id)}/reference`
  );
};

export const resolveDefaultBankAccountId = async (): Promise<string | null> => {
  const response = await getBankAccounts(1, 50);
  const accounts = response?.bankAccounts ?? [];
  if (!accounts.length) {
    return null;
  }
  const primary = accounts.find((account) => account?.isPrimary === true);
  const selected = primary ?? accounts[0];
  return typeof selected?._id === "string" && selected._id
    ? selected._id
    : null;
};

export const setPrimaryBankAccount = async (id: string): Promise<unknown> => {
  return create(`/api/v2/banking/bank-accounts/${id}/primary`);
};

export const getUserBanking = async (_userId?: string) => getBankAccounts();

export const getUserBank = async (bankId: string) => getBankAccount(bankId);

export const getBanks = async (): Promise<BankInfo[]> => {
  const response = await get<BankInfo[] | { results?: BankInfo[] } | null>(
    "/api/v2/banking/banks"
  );
  if (Array.isArray(response)) return response;
  if (response && Array.isArray(response.results)) return response.results;
  return [];
};

export const getEightyEightBank = async () =>
  get("/api/v2/banking/platform-bank");
